import type { FreeformGeometry, FreeformLayout, GridPlacement, ViewportKey } from './types';

export const FOOTER_FREEFORM_PLACEMENT_VERSION = 2 as const;
export const FOOTER_FREEFORM_GRID_COLUMNS = 12;
export const FOOTER_FREEFORM_SNAP_THRESHOLD = 6;
export const FOOTER_FREEFORM_BOTTOM_PADDING = 48;
export const FOOTER_FREEFORM_DEFAULT_ELEMENT_HEIGHT = 96;

/**
 * Footer height is an authored integer minimum.  DOM measurements are
 * rendered output and must never be persisted through this function.
 */
export function normalizeFooterMinimumHeight(value: number | null | undefined): number | undefined {
  if (value == null || !Number.isFinite(value)) return undefined;
  return Math.round(value);
}

export function configuredFooterMinimumHeight(value: number | null | undefined): number | undefined {
  const normalized = normalizeFooterMinimumHeight(value);
  return normalized == null ? undefined : Math.max(160, Math.min(1000, normalized));
}

/**
 * Resolve the rendered surface height without changing the authored minimum.
 * `renderedContentHeight` is measured output and is deliberately a separate
 * argument from the persisted setting.
 */
export function resolveFooterRenderedHeight(
  configuredMinimumHeight: number | null | undefined,
  contentBottom: number,
  renderedContentHeight = 0,
): number {
  return Math.ceil(Math.max(
    normalizeFooterMinimumHeight(configuredMinimumHeight) ?? 0,
    Number.isFinite(contentBottom) ? contentBottom + FOOTER_FREEFORM_BOTTOM_PADDING : 0,
    Number.isFinite(renderedContentHeight) ? renderedContentHeight : 0,
  ));
}

/** The same surface geometry is used when a footer is rendered at either desktop width. */
export function centeredFreeformX(surfaceWidth: number, elementWidth: number): number {
  return Math.max(0, (surfaceWidth - elementWidth) / 2);
}

export function geometryForViewport(
  layout: FreeformLayout | undefined,
  viewport: ViewportKey,
  fallback: FreeformGeometry,
): FreeformGeometry {
  if (!layout || layout.placementVersion !== FOOTER_FREEFORM_PLACEMENT_VERSION) return fallback;
  return layout[viewport] ?? layout.desktop ?? fallback;
}

export function fallbackFreeformGeometry(index: number, viewport: ViewportKey): FreeformGeometry {
  const columns = viewport === 'desktop' ? 3 : viewport === 'tablet' ? 2 : 1;
  const width = viewport === 'desktop' ? 320 : viewport === 'tablet' ? 300 : 280;
  const gap = viewport === 'mobile' ? 24 : 32;
  return {
    x: (index % columns) * (width + gap),
    y: Math.floor(index / columns) * 120,
    width,
    heightMode: 'auto',
    layer: 'normal',
  };
}

export function layerToZIndex(layer: FreeformGeometry['layer']): number {
  return layer === 'foreground' ? 2 : layer === 'background' ? 0 : 1;
}

export function logicalDelta(screenDelta: number, canvasScale: number): number {
  return screenDelta / Math.max(canvasScale, 0.01);
}

export function snapCoordinate(value: number, guides: number[], threshold = FOOTER_FREEFORM_SNAP_THRESHOLD): number {
  const nearest = guides.reduce<{ value: number; distance: number } | null>((best, guide) => {
    const distance = Math.abs(value - guide);
    return !best || distance < best.distance ? { value: guide, distance } : best;
  }, null);
  return nearest && nearest.distance <= threshold ? nearest.value : value;
}

export function footerGridGuides(surfaceWidth: number): number[] {
  return Array.from({ length: FOOTER_FREEFORM_GRID_COLUMNS + 1 }, (_, index) =>
    (surfaceWidth / FOOTER_FREEFORM_GRID_COLUMNS) * index,
  );
}

export function snapFreeformX(x: number, width: number, surfaceWidth: number): number {
  const left = snapCoordinate(x, footerGridGuides(surfaceWidth));
  const right = snapCoordinate(x + width, footerGridGuides(surfaceWidth));
  if (right !== x + width) return right - width;
  return left;
}

export function measuredFreeformGeometry(
  elementRect: Pick<DOMRect, 'left' | 'top' | 'width' | 'height'>,
  surfaceRect: Pick<DOMRect, 'left' | 'top'>,
  canvasScale: number,
  layer: FreeformGeometry['layer'] = 'normal',
): FreeformGeometry {
  const scale = Math.max(canvasScale, 0.01);
  return {
    x: Math.max(0, Math.round((elementRect.left - surfaceRect.left) / scale)),
    y: Math.max(0, Math.round((elementRect.top - surfaceRect.top) / scale)),
    width: Math.max(40, Math.round(elementRect.width / scale)),
    heightMode: 'auto',
    layer,
  };
}

export function legacyPlacementForMigration(
  placement: GridPlacement,
  elementRect: Pick<DOMRect, 'left' | 'top' | 'width' | 'height'>,
  surfaceRect: Pick<DOMRect, 'left' | 'top'>,
  canvasScale: number,
): FreeformGeometry {
  // The placement argument is intentionally not used to guess coordinates.
  // A migration is valid only when the browser can measure the actual v1
  // rendered rectangle. Keeping the parameter documents the legacy source
  // and makes callers prove that they migrated a v1 element.
  void placement;
  return measuredFreeformGeometry(elementRect, surfaceRect, canvasScale);
}
